import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, X, Minus, Plus, Trash2 } from 'lucide-react';
import { useCart } from '../context/CartContext';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1594035910387-fea47794261f?w=200';

const CartBubble = () => {
  const { items, totals, gifts, loading, updateQuantity, removeItem, getItemCount } = useCart();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const itemCount = getItemCount();

  // Bloquear el scroll de la página de fondo mientras el panel está abierto
  // (si no, el scroll "se filtra" hacia la página en vez de quedarse en la lista de items)
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const formatPrice = (price) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(price);

  // Total sin IVA (IVA absorbido por la empresa), igual que en Cart.js
  const getFinalTotal = () => {
    const subtotalAfterDiscount = totals.subtotal - (totals.discount_amount || 0) - (totals.promo_discount || 0);
    return subtotalAfterDiscount;
  };

  // Promo de regalos: si ya calificó pero falta elegir el 10ml, se resuelve en la página del carrito
  const giftPending = !!(gifts && gifts.applied && !gifts.complete);
  const giftTeaser = !!(gifts && !gifts.applied && gifts.qualifying_quantity >= 1);

  const goToCheckout = () => {
    setIsOpen(false);
    navigate('/checkout');
  };

  const goToFullCart = () => {
    setIsOpen(false);
    navigate('/cart');
  };

  return (
    <>
      {/* Floating Cart Button — arriba del botón de WhatsApp */}
      <div className="fixed bottom-[152px] right-4 z-[55] sm:bottom-[104px] sm:right-6">
        <button
          onClick={() => setIsOpen(true)}
          className="group relative flex items-center justify-center"
          aria-label="Ver carrito"
          data-testid="cart-bubble-button"
        >
          <div className="relative w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-brand-cobalt to-brand-magenta rounded-full flex items-center justify-center shadow-lg shadow-[#1B4FD8]/30 group-hover:shadow-[#1B4FD8]/50 group-hover:scale-110 transition-all duration-300">
            <ShoppingCart className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>

          {itemCount > 0 && (
            <span
              className="absolute -top-1 -right-1 bg-brand-magenta text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-md"
              data-testid="cart-bubble-count"
            >
              {itemCount > 99 ? '99+' : itemCount}
            </span>
          )}
        </button>
      </div>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[60] bg-brand-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setIsOpen(false)}
          />

          {/* Panel — bottom sheet en móvil, tarjeta anclada a la burbuja en desktop */}
          <div
            className="fixed z-[61] bg-white shadow-luxury-xl flex flex-col
                       inset-x-0 bottom-0 rounded-t-2xl max-h-[85vh] cart-bubble-slide-up
                       sm:inset-x-auto sm:bottom-[104px] sm:right-6 sm:rounded-2xl sm:w-[400px] sm:max-h-[75vh] sm:animate-scale-in"
            data-testid="cart-bubble-panel"
            data-lenis-prevent
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-brand-black/10 flex-shrink-0">
              <h3 className="font-display text-lg text-brand-black">
                Tu Carrito {itemCount > 0 && `(${itemCount})`}
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-brand-black/50 hover:text-brand-black p-1 transition-colors"
                aria-label="Cerrar"
                data-testid="cart-bubble-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Items */}
            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center py-12 px-4 text-center">
                <ShoppingCart className="w-12 h-12 text-brand-cobalt/20 mb-3" />
                <p className="text-brand-black/50 text-sm">Tu carrito está vacío</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-4 space-y-3">
                {items.map((item) => (
                  <div
                    key={`${item.product_id}-${item.size_ml || 100}`}
                    className="flex gap-3"
                    data-testid={`cart-bubble-item-${item.product_id}-${item.size_ml || 100}`}
                  >
                    <div className="w-16 h-16 flex-shrink-0 bg-brand-black/5 overflow-hidden rounded">
                      <img
                        src={item.main_image || FALLBACK_IMAGE}
                        alt={item.product_name || 'Producto'}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-brand-cobalt/60 uppercase tracking-wider truncate">
                        {item.inspired_by_brand}
                      </p>
                      <h4 className="text-xs font-medium text-brand-black leading-tight line-clamp-2">
                        {item.product_code} - {item.product_name}
                      </h4>

                      <div className="flex items-center justify-between mt-1.5">
                        <div className="flex items-center border border-brand-black/20 rounded flex-shrink-0">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.size_ml || 100)}
                            disabled={item.quantity <= 1}
                            className="p-1 text-brand-black hover:bg-brand-cobalt/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            aria-label="Disminuir cantidad"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="w-5 text-center text-xs font-medium text-brand-black">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.size_ml || 100)}
                            className="p-1 text-brand-black hover:bg-brand-cobalt/5 transition-colors"
                            aria-label="Aumentar cantidad"
                          >
                            <Plus size={12} />
                          </button>
                        </div>

                        <p className="font-semibold text-xs text-brand-black flex-shrink-0">
                          {formatPrice(item.price * item.quantity)}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => removeItem(item.product_id, item.size_ml || 100)}
                      className="text-brand-black/30 hover:text-brand-magenta p-1 flex-shrink-0 self-start transition-colors"
                      aria-label="Eliminar producto"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            {items.length > 0 && (
              <div className="border-t border-brand-black/10 p-4 sm:p-5 flex-shrink-0">
                <div className="flex justify-between items-baseline mb-3">
                  <span className="text-sm text-brand-black/60">Total</span>
                  <span className="font-display text-lg font-bold text-brand-black">
                    {formatPrice(getFinalTotal())}
                  </span>
                </div>

                {giftPending && (
                  <button
                    onClick={goToFullCart}
                    className="w-full mb-2 p-2 text-xs text-center rounded border border-brand-cobalt/40 bg-brand-cobalt/5 text-brand-cobalt"
                    data-testid="cart-bubble-gift-pending"
                  >
                    🎁 Elige tu perfume de 10ml de regalo para continuar
                  </button>
                )}
                {giftTeaser && (
                  <p className="mb-2 text-xs text-center text-brand-black/60" data-testid="cart-bubble-gift-teaser">
                    🎁 Promo {gifts.promo_name}: llevas {gifts.qualifying_quantity} de {gifts.buy_quantity} perfumes de {gifts.buy_size_ml}ml
                  </p>
                )}

                <button
                  onClick={goToCheckout}
                  disabled={loading || giftPending}
                  className="w-full brand-btn disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="cart-bubble-checkout-btn"
                >
                  Ir al Checkout
                </button>

                <button
                  onClick={goToFullCart}
                  className="w-full mt-2 text-xs text-brand-black/50 hover:text-brand-cobalt text-center py-1 transition-colors"
                >
                  Ver carrito completo
                </button>
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes cart-bubble-slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .cart-bubble-slide-up {
          animation: cart-bubble-slide-up 0.3s ease-out forwards;
        }
        @media (min-width: 640px) {
          .cart-bubble-slide-up {
            animation: none;
          }
        }
      `}</style>
    </>
  );
};

export default CartBubble;
